/**
 * ContractEntryPage — Phase 6a.10 per Spec §21.7.1.
 *
 * Wraps `<ContractFormContainer>` (Phase 6a.8) inside the engagement
 * navigation shell: header bar above, footer-mirror buttons below. The
 * form is hydrated from `engagement.contracts[idx]` and saved back via
 * `useEngagementContractDraft` (500 ms debounce + flush on navigation).
 *
 * Phase 6e.4.2-hotfix-2 (Bug D) — soft-delete is gone. The Prev/Next
 * walk no longer needs to hop over "zombi" slots, and the `deleted-slot`
 * load state is unreachable. A destructive Sil button is added to the
 * footer so the auditor can hard-delete the currently-open contract
 * without going back to the list.
 *
 * Six Golden Rules — Rule 2: lenient navigation. Önceki / Sonraki /
 * Listeyi Görüntüle never block; they call `flushSave()` and redirect
 * silently. The form state lives in localStorage continuously.
 */

import { ChevronLeft, ChevronRight, List, Trash2 } from "lucide-react";
import * as React from "react";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ContractFormContainer } from "@/features/contract-form";
import type { ContractFormValues } from "@/features/contract-form/schemas/contractFormSchema";
import { DeleteConfirmDialog } from "@/features/engagement/components/DeleteConfirmDialog";
import { EngagementHeader } from "@/features/engagement/components/EngagementHeader";
import { findAdjacentContractIndex } from "@/features/engagement/contract-deletion";
import { useEngagementAccess } from "@/features/engagement/useEngagementAccess";
import { useEngagementContractDraft } from "@/features/engagement/useEngagementContractDraft";
import { useHistoryStorage } from "@/services";
import type { EngagementHistoryEntry } from "@/types";

export function ContractEntryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, idx } = useParams<{ id: string; idx: string }>();
  const historyStorage = useHistoryStorage();
  const [engagement, setEngagement] =
    React.useState<EngagementHistoryEntry | null>(null);
  const [loadState, setLoadState] = React.useState<
    "loading" | "loaded" | "missing" | "out-of-range"
  >("loading");
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const formRef = React.useRef<UseFormReturn<ContractFormValues> | null>(null);

  const contractIdx = Number.parseInt(idx ?? "0", 10);
  const safeIdx = Number.isFinite(contractIdx) ? contractIdx : -1;

  // SPEC-SHARE-B2 — viewers (shared, role=viewer) get a read-only form:
  // the fieldset disables every input, draft auto-save is suppressed, and
  // the destructive delete control is hidden. Fail-safe: TRUE while loading.
  const { readOnly } = useEngagementAccess(id);

  const draft = useEngagementContractDraft(id ?? "", safeIdx);

  // Hydrate the engagement from localStorage.
  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!id) {
        if (!cancelled) setLoadState("missing");
        return;
      }
      const e = await historyStorage.getEngagement(id);
      if (cancelled) return;
      if (!e) {
        setLoadState("missing");
        return;
      }
      if (safeIdx < 0 || safeIdx >= e.contracts.length) {
        setEngagement(e);
        setLoadState("out-of-range");
        return;
      }
      setEngagement(e);
      setLoadState("loaded");
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [id, safeIdx, historyStorage]);

  // Redirect on missing or out-of-range.
  React.useEffect(() => {
    if (loadState === "missing") {
      navigate("/history", { replace: true });
    } else if (loadState === "out-of-range" && engagement) {
      navigate(`/engagements/${engagement.id}/contracts`, { replace: true });
    }
  }, [loadState, engagement, navigate]);

  // Lenient navigation handlers. Always flush pending draft before
  // navigating so the destination page reads fresh data.
  async function goTo(targetIdx: number) {
    await draft.flushSave();
    navigate(`/engagements/${id}/contracts/${targetIdx}`);
  }

  async function goToList() {
    await draft.flushSave();
    navigate(`/engagements/${id}/contracts`);
  }

  // Phase 6e.4.2-hotfix-2 Bug D — hard delete from the detail page.
  // Reuses the same DeleteConfirmDialog as the list page; on confirm,
  // splices the slot from the engagement and navigates back to the
  // (now-shorter) contract list with a toast.
  async function handleDeleteFromDetail() {
    if (!engagement) return;
    setShowDeleteConfirm(false);
    try {
      await historyStorage.deleteContract(engagement.id, safeIdx);
      toast.success(t("engagement.contract.delete.toast"));
      navigate(`/engagements/${engagement.id}/contracts`);
    } catch (err) {
      console.error("[contracts] deleteContract failed", err);
    }
  }

  if (loadState !== "loaded" || !engagement) {
    return (
      <div className="mx-auto max-w-4xl">
        <p className="text-muted-foreground">{t("common.loading")}</p>
      </div>
    );
  }

  // Prev / Next are simple bounds-checked steps now that soft-delete is
  // gone (Phase 6e.4.2-hotfix-2). The prop names on `EngagementHeader`
  // stay `prevLiveIdx` / `nextLiveIdx` to avoid touching its API in
  // this hotfix; the semantics now match a plain ±1 walk.
  const prevLiveIdx = findAdjacentContractIndex(
    engagement.contracts.length,
    safeIdx,
    "prev",
  );
  const nextLiveIdx = findAdjacentContractIndex(
    engagement.contracts.length,
    safeIdx,
    "next",
  );
  const canGoPrev = prevLiveIdx !== null;
  const canGoNext = nextLiveIdx !== null;
  const initialValues = (engagement.contracts[safeIdx] ??
    undefined) as Partial<ContractFormValues> | undefined;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <EngagementHeader
        engagement={engagement}
        currentContractIdx={safeIdx}
        prevLiveIdx={prevLiveIdx}
        nextLiveIdx={nextLiveIdx}
        onNavigate={(targetIdx) => {
          void goTo(targetIdx);
        }}
        onReviewList={() => {
          void goToList();
        }}
      />

      <ContractFormContainer
        key={`${engagement.id}-${safeIdx}`}
        initialValues={initialValues}
        functionalCurrency={engagement.functionalCurrency}
        auditPeriodEnd={engagement.auditPeriodEnd}
        auditPeriodStart={engagement.auditPeriodStart}
        readOnly={readOnly}
        onValuesChange={readOnly ? undefined : draft.handleValuesChange}
        onFormReady={(form) => {
          formRef.current = form;
        }}
      />

      <Separator />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          type="button"
          variant="outline"
          disabled={!canGoPrev}
          onClick={() => {
            if (prevLiveIdx !== null) void goTo(prevLiveIdx);
          }}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {t("engagement.contract.navigation.previous")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            void goToList();
          }}
        >
          <List className="h-4 w-4 mr-1" />
          {t("engagement.contract.navigation.reviewList")}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={!canGoNext}
          onClick={() => {
            if (nextLiveIdx !== null) void goTo(nextLiveIdx);
          }}
        >
          {t("engagement.contract.navigation.next")}
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Phase 6e.4.2-hotfix-2 Bug D — destructive delete button.
          SPEC-SHARE-B2 — hidden (with its dialog) for read-only viewers. */}
      {!readOnly && (
        <>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
              data-testid="contract-detail-delete-button"
            >
              <Trash2 className="h-4 w-4 mr-1" aria-hidden="true" />
              {t("engagement.contract.delete.button")}
            </Button>
          </div>

          <DeleteConfirmDialog
            engagement={engagement}
            pendingDeleteIndex={showDeleteConfirm ? safeIdx : null}
            onCancel={() => setShowDeleteConfirm(false)}
            onConfirm={() => {
              void handleDeleteFromDetail();
            }}
          />
        </>
      )}
    </div>
  );
}
